/**
 * The text screen (handoff §9, §16.2, §19.7): one saved text, read in full.
 *
 * The title, the decks it is linked to (a tap opens the deck) or «Без колоды», the
 * meta line, the text's level, the whole text with its gaps drawn as blanks (never as
 * answers), the waiting-words card with its add action, the card-generation status of
 * a save that has just happened, practice, edit mode, sharing (§17.1) and delete.
 *
 * Reached from the Library's and a deck's text lists, and as the creation flow's
 * landing: a save arrives with its result in the router state (the toast, and the
 * generation run to watch). A shared link lands here too (§17.2): with a copy just
 * saved (its toast), or a copy saved before (the one-visit banner). Back returns to where the learner came from (`from` in
 * the state), else to the Library.
 *
 * Controls are not selectable text and the text itself is (index.css, `.cs-ui`).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, Info, Layers, Share2 } from 'lucide-react'
import { useLocale } from '../i18n/context'
import { useText } from '../hooks/useTexts'
import { useIsDesktop } from '../hooks/useIsDesktop'
import { useScreenHeader } from '../hooks/useScreenHeader'
import { useTextFlowStore } from '../stores/textFlowStore'
import { CONTROL_SURFACE, suppressTouchContextMenu } from '../utils/controlSurface'
import type { TextDetail, TextFlowSaveResult } from '../types'
import { DECK_TOP_STRIP_CLEARANCE } from '../components/DeckTopStrip'
import GappedText from '../components/texts/GappedText'
import GenerationWatcher from '../components/texts/GenerationWatcher'
import TextLevelCard from '../components/texts/TextLevelCard'
import TextWaitingCard from '../components/texts/TextWaitingCard'
import { DeleteFlow, TextRowMenu } from '../components/texts/TextList'
import { useSaveToast } from '../components/texts/useSaveToast'
import { Eyebrow, GhostButton, PrimaryButton, SecondaryPill } from '../components/texts/ui'
import PlayerSheet from '../components/texts/player/PlayerSheet'
import TextShareSheet from '../components/texts/share/TextShareSheet'
import { toast } from '../components/shared/Toast'
import { usePracticeActions } from '../hooks/useTextPractice'
import { practiceStatus } from '../utils/textPractice'
import { TEXT_LEVEL_MAX } from '../components/texts/TextLevelCard'

/** What a screen that opens a text puts in the router state. */
interface TextRouteState {
  /** The URL to go back to. */
  from?: string
  /** What the back link calls it, when the URL alone does not say (a deck's title). */
  fromLabel?: string
  /** A save from the creation flow, or an edit that made cards: its result, for the toast and the generation run. */
  saved?: TextFlowSaveResult
  /** The save was an edit's (ADR-115 Amendment 3): the creation flow's draft is not this save's to clear. */
  edited?: boolean
  /** From a shared link (§17.2): the copy was saved just now, or had been before. */
  sharedCopy?: 'saved' | 'existing'
}

export default function TextPage() {
  const { textId } = useParams<{ textId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { tr, tag } = useLocale()
  const toastSave = useSaveToast()

  // The save is consumed once. It is read into state, then taken out of the history
  // entry, so a reload shows the text and not the same toast and run again.
  const [arrival] = useState<TextRouteState>(() => (location.state as TextRouteState | null) ?? {})
  const saved = arrival.saved ?? null
  const consumed = useRef(false)
  useEffect(() => {
    if (!saved || consumed.current) return
    consumed.current = true
    // The save is made: the flow starts clean next time. An edit's save leaves the
    // flow alone: a creation draft set aside for edit mode's picker is still the learner's.
    if (!arrival.edited) useTextFlowStore.getState().reset()
    toastSave(saved)
    navigate(location.pathname, { replace: true, state: { from: arrival.from, fromLabel: arrival.fromLabel } })
  }, [saved, toastSave, navigate, location.pathname, arrival.from, arrival.fromLabel, arrival.edited])

  // A shared link's landing, consumed the same way: the banner is for this visit only.
  const sharedCopy = arrival.sharedCopy ?? null
  const sharedConsumed = useRef(false)
  useEffect(() => {
    if (!sharedCopy || sharedConsumed.current) return
    sharedConsumed.current = true
    if (sharedCopy === 'saved') toast.success(tr.textShare.saved)
    navigate(location.pathname, { replace: true, state: { from: arrival.from, fromLabel: arrival.fromLabel } })
  }, [sharedCopy, tr.textShare.saved, navigate, location.pathname, arrival.from, arrival.fromLabel])
  const [sharing, setSharing] = useState(false)

  const { data: text, isLoading, isError, error, refetch } = useText(textId)
  // The generation run to watch: the save's, then the latest add's.
  const [addedRun, setAddedRun] = useState<string | null>(null)
  const runId = addedRun ?? saved?.text_import_id ?? null
  const [deleting, setDeleting] = useState(false)

  const backTo = arrival.from ?? '/library'
  const backLabel = arrival.fromLabel
    ?? (arrival.from?.startsWith('/home') ? tr.nav.home
      : arrival.from?.startsWith('/decks/') ? tr.textScreen.backDeck
      : arrival.from ? tr.textList.segTexts : tr.nav.library)
  const goBack = useCallback(() => navigate(backTo), [navigate, backTo])
  // The player and edit mode keep this screen's origin, so leaving them comes back here
  // with the same back link.
  const goPractice = () =>
    navigate(`/texts/${textId}/practice`, { state: { from: arrival.from, fromLabel: arrival.fromLabel } })
  const practiceActions = usePracticeActions(textId ?? '')
  const [confirmRestart, setConfirmRestart] = useState(false)
  // «Начать заново» (§9.2): asks first only when the attempt has answers (§18).
  const restartNow = () => {
    setConfirmRestart(false)
    practiceActions.restart.mutate(undefined, { onSuccess: goPractice })
  }
  const goEdit = () =>
    navigate(`/texts/${textId}/edit`, { state: { from: arrival.from, fromLabel: arrival.fromLabel } })

  const isDesktop = useIsDesktop()
  const meta = text ? metaLine(text, tr, tag) : ''
  // The desktop bar carries back and identity only (handoff §19.0, header budget).
  useScreenHeader(
    useMemo(
      () => (text ? { back: { label: backLabel, onClick: goBack }, title: text.title, subtitle: meta } : null),
      [text, backLabel, goBack, meta],
    ),
  )

  if (isLoading) return <div aria-busy="true" style={{ minHeight: 240 }} />
  // Deleted, another learner's, or never there: the server answers 404 alike.
  if ((error as { response?: { status?: number } } | null)?.response?.status === 404) {
    return <Navigate to="/library" replace />
  }
  if (isError || !text) {
    return (
      <div role="alert" style={{ textAlign: 'center', padding: '64px 16px', fontFamily: 'var(--font-ui)' }}>
        <p style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{tr.textScreen.loadFailed}</p>
        <SecondaryPill onClick={() => refetch()}>{tr.textScreen.retry}</SecondaryPill>
      </div>
    )
  }

  const hasGaps = text.gaps.length > 0
  const running = text.practice?.in_progress ?? null
  const onRestart = () => (running && running.answered > 0 ? setConfirmRestart(true) : restartNow())
  const restartSheet = confirmRestart && running && (
    <PlayerSheet
      testId="text-restart-sheet"
      danger
      title={tr.textPlayer.restartTitle}
      primary={{ label: tr.textPlayer.restartConfirm, onClick: restartNow, testId: 'text-restart-confirm' }}
      secondary={{ label: tr.textPlayer.restartStay, onClick: () => { setConfirmRestart(false); goPractice() }, testId: 'text-restart-stay' }}
    >
      <p style={{ margin: 0 }}>{tr.textPlayer.restartBody(running.answered, running.total)}</p>
      <p style={{ margin: 0 }}>{tr.textPlayer.restartKeep(text.level, TEXT_LEVEL_MAX)}</p>
    </PlayerSheet>
  )
  const practiceButtons = (height: number) => hasGaps && (
    running ? (
      <>
        <PrimaryButton full height={height} onClick={goPractice} data-testid="text-practice-resume">{tr.textPlayer.resume}</PrimaryButton>
        <GhostButton onClick={onRestart} data-testid="text-practice-restart">{tr.textPlayer.restart}</GhostButton>
      </>
    ) : (
      <PrimaryButton full height={height} onClick={goPractice} data-testid="text-practice-start">{tr.textPlayer.train}</PrimaryButton>
    )
  )
  const status = runId && (
    <GenerationWatcher
      key={runId}
      textImportId={runId}
      deckId={(addedRun ? null : saved?.deck_id) ?? text.deck_ids[0] ?? null}
      title={text.title}
    />
  )
  const waiting = text.waiting_words.length > 0 && (
    <TextWaitingCard
      textId={text.id}
      words={text.waiting_words}
      decks={text.decks}
      onRun={setAddedRun}
    />
  )
  const body = (
    <div
      data-testid="text-body"
      style={{
        padding: isDesktop ? '28px 36px' : '16px 18px', borderRadius: 16, background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
      }}
    >
      {/* The text itself is content to read, so it is selectable; its gaps are blanks, never answers. */}
      <p style={{
        margin: 0, fontFamily: 'var(--font-content)', color: 'var(--text-primary)', whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere', fontSize: isDesktop ? 18 : 16, lineHeight: isDesktop ? '36px' : '30px',
      }}>
        <GappedText
          text={text.body}
          spans={text.gaps.flatMap((g) => g.occurrences)}
          blankWidth={isDesktop ? 56 : 44}
          blankTestId="text-body-blank"
        />
      </p>
    </div>
  )
  const shareSheet = sharing && <TextShareSheet textId={text.id} onClose={() => setSharing(false)} />
  const copyBanner = sharedCopy === 'existing' && (
    <div
      role="status"
      data-testid="text-copy-banner"
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', borderRadius: 8,
        background: 'var(--accent-soft)', color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', fontSize: 13, lineHeight: 1.45,
      }}
    >
      <Info size={16} aria-hidden style={{ flexShrink: 0, marginTop: 1, color: 'var(--accent)' }} />
      {tr.textShare.alreadyCopy}
    </div>
  )
  const confirm = deleting && (
    <DeleteFlow
      text={text}
      onClose={() => setDeleting(false)}
      onDeleted={() => navigate(backTo, { replace: true })}
    />
  )

  if (isDesktop) {
    return (
      <div data-testid="text-screen" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 24, alignItems: 'start' }}>
        {copyBanner && <div style={{ gridColumn: '1 / -1' }}>{copyBanner}</div>}
        {body}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {hasGaps && <TextLevelCard level={text.level} compact />}
          <DecksCard text={text} />
          {waiting}
          {status}
          {running && (
            <p data-testid="text-practice-status" style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
              {practiceStatus(text.practice, text.gaps.length, tr, tag, 'long')}
            </p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>{practiceButtons(48)}</div>
          <SecondaryPill full height={40} onClick={goEdit} data-testid="text-edit">{tr.textEdit.menuEdit}</SecondaryPill>
          <SecondaryPill full height={40} onClick={() => setSharing(true)} data-testid="text-share">
            <Share2 size={16} aria-hidden />
            {tr.textShare.share}
          </SecondaryPill>
          <button
            type="button"
            className={CONTROL_SURFACE}
            data-testid="text-delete"
            onClick={() => setDeleting(true)}
            style={{
              alignSelf: 'center', marginTop: 12, minHeight: 40, padding: '0 12px', background: 'none',
              border: 'none', cursor: 'pointer', fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--text-secondary)',
            }}
          >
            {tr.textList.deleteText}
          </button>
        </div>
        {confirm}
        {restartSheet}
        {shareSheet}
      </div>
    )
  }

  return (
    <div data-testid="text-screen">
      {/* Reserves the strip's band: the strip is out of flow (it is the exit, so it never scrolls). */}
      <div aria-hidden style={{ height: DECK_TOP_STRIP_CLEARANCE }} />
      <TopStrip label={backLabel} onBack={goBack} title={text.title} onDelete={() => setDeleting(true)} onEdit={goEdit} onShare={() => setSharing(true)} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 24 }}>
        {copyBanner}
        <h1 style={{
          margin: '8px 0 0', fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.25,
          color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', overflowWrap: 'anywhere',
        }}>
          {text.title}
        </h1>
        <DeckChips text={text} />
        <p data-testid="text-meta" style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}>{meta}</p>
        <div className={CONTROL_SURFACE} style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <SecondaryPill height={36} onClick={goEdit} data-testid="text-edit" style={{ fontSize: 13, padding: '0 14px' }}>
            {tr.textEdit.menuEdit}
          </SecondaryPill>
          <SecondaryPill height={36} onClick={() => setSharing(true)} data-testid="text-share" style={{ fontSize: 13, padding: '0 14px' }}>
            <Share2 size={15} aria-hidden />
            {tr.textShare.share}
          </SecondaryPill>
        </div>
        {waiting}
        {status}
        {hasGaps && <TextLevelCard level={text.level} />}
        {body}
      </div>
      {hasGaps && (
        <>
          {/* Reserves the bar's band, so the text's end is never under it. */}
          <div aria-hidden style={{ height: 84 }} />
          <div
            className={CONTROL_SURFACE}
            data-testid="text-practice-bar"
            style={{
              position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 25, display: 'flex', alignItems: 'center', gap: 8,
              flexDirection: 'row-reverse', padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))',
              background: 'var(--bg-chrome)', borderTop: '1px solid var(--border-chrome)',
            }}
          >
            {practiceButtons(52)}
          </div>
        </>
      )}
      {confirm}
      {restartSheet}
      {shareSheet}
    </div>
  )
}

/** «{n} пропусков · {w} слов · {статус}»: a text with no gaps has nothing to practise, so no status. */
function metaLine(text: TextDetail, tr: ReturnType<typeof useLocale>['tr'], tag: string): string {
  return [
    text.gaps.length > 0 ? tr.textList.gaps(text.gaps.length) : tr.textList.noGaps,
    tr.deckTexts.words(text.word_count),
    practiceStatus(text.practice, text.gaps.length, tr, tag, 'long'),
  ].filter(Boolean).join(' · ')
}

/** The thin top strip (handoff §0.3): ‹ and the origin's name, and the screen's «···». */
function TopStrip({ label, onBack, title, onDelete, onEdit, onShare }: { label: string; onBack: () => void; title: string; onDelete: () => void; onEdit: () => void; onShare: () => void }) {
  return (
    <div
      className={`md:hidden ${CONTROL_SURFACE}`}
      onContextMenu={suppressTouchContextMenu}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 25, height: DECK_TOP_STRIP_CLEARANCE,
        paddingTop: 'env(safe-area-inset-top, 0px)', paddingRight: 4, display: 'flex', alignItems: 'center', gap: 4,
        background: 'var(--bg-chrome)', borderBottom: '1px solid var(--border-chrome)',
      }}
    >
      <button
        type="button"
        onClick={onBack}
        aria-label={label}
        data-testid="text-back"
        style={{
          flexShrink: 0, width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', padding: 0,
        }}
      >
        <ChevronLeft size={24} />
      </button>
      <span style={{
        flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {label}
      </span>
      <TextRowMenu title={title} onDelete={onDelete} onEdit={onEdit} onShare={onShare} />
    </div>
  )
}

/** Linked decks as 30 px pills that open the deck, or the outlined «Без колоды». */
function DeckChips({ text }: { text: TextDetail }) {
  const { tr } = useLocale()
  const navigate = useNavigate()
  const common = {
    display: 'inline-flex', alignItems: 'center', gap: 6, height: 30, padding: '0 12px',
    borderRadius: 'var(--radius-pill)', fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 500, maxWidth: '100%',
  } as const
  return (
    <div className={CONTROL_SURFACE} style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {text.decks.length === 0 ? (
        <span data-testid="text-no-deck" style={{ ...common, border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
          {tr.textList.noDeck}
        </span>
      ) : text.decks.map((d) => (
        <button
          key={d.id}
          type="button"
          data-testid="text-deck-chip"
          onClick={() => navigate(`/decks/${d.id}`)}
          style={{ ...common, border: 'none', cursor: 'pointer', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
        >
          <Layers size={14} aria-hidden style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title}</span>
        </button>
      ))}
    </div>
  )
}

/** §19.7's card with the eyebrow «Колоды». */
function DecksCard({ text }: { text: TextDetail }) {
  const { tr } = useLocale()
  return (
    <section
      className={CONTROL_SURFACE}
      style={{
        padding: '12px 14px', borderRadius: 16, background: 'var(--bg-surface)', border: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column', gap: 8,
      }}
    >
      <Eyebrow>{tr.textScreen.decksEyebrow}</Eyebrow>
      <DeckChips text={text} />
    </section>
  )
}
